import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import TableTab from "../components/tabs/table-tab";
import allReportStatus from "../constants/all-report-status";
import { CARD_FORMATS } from "../constants/menu-types";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import { TranslationKeys } from "../contexts/TranslationKeys";
import columns from "../models/ColumnDefinitions/CardFormatsColumns";
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import searchColumns from "../models/search/CardFormatsSearch";
import SiteSearch from "../models/search/SiteSearch";
import { deleteTruncationFormat, getCardFormats } from "../services/cardFormatsService";
import { getSiteNamesWithPagination } from "../services/siteService";
import CustomSettingIcon from '../images/Custom Setting.svg';
import InheritPresetIcon from '../images/Inherited from Preset.svg';
import { TableLegendItem } from "../models/table/TableLegendItem";
import { DEF_PG_SIZE_LIST_WO_ADD } from '../constants/maximum-page-size';
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { Category } from "../models/Category";
import { Filter } from "../models/Filter";
import TableContextStateMonitor from "../contexts/table/TableContextStateMonitor";

interface Props {
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    setCategories: (category: Category[]) => void,
    onFilterChange: (filters: Filter[]) => void
}

const CardFormatTab: FC<Props> = ({setBusy, categories, setCategories, onFilterChange}) => {
    const { translate } = useLocale();

    useEffect(() => {
        (async () => {
            setLookup([{
                name: LookupTypes.site,
                values: [],
                dataMethod: getSiteNamesWithPagination,
                searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
            }]);
        })();
    }, []);

    const title = translate.byKey(TranslationKeys.cardFormats);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);    
    const itemPath = "/access-control/configuration/card-format/";
    //user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const initialiseUserPreferences = (setDetails: (userLoginId, CARD_FORMATS) => void) => {
        setDetails(userLoginId, CARD_FORMATS);
    }
    // <-- end context

    const tableLegendItems: TableLegendItem[] = [
        {
            value: allReportStatus.Custom.id,
            imageIcon: CustomSettingIcon,
            label: translate.byKey(allReportStatus.Custom.translationKey) ?? allReportStatus.Custom.label,
            tooltip: translate.byKey(allReportStatus.Custom.translationKey) ?? allReportStatus.Custom.label
        },
        {
            value: allReportStatus.Template.id,
            imageIcon: InheritPresetIcon,
            label: translate.byKey(allReportStatus.Template.translationKey) ?? allReportStatus.Template.label,
            tooltip: translate.byKey(allReportStatus.Template.translationKey) ?? allReportStatus.Template.label
        }
    ];

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteTruncationFormat(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) - 1 }]);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }


    return <TableContextProvider
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        dataMethod={getCardFormats}
        tableDescriptor={columns}
        searchDescriptor={searchColumns}
        legendItems={tableLegendItems}
        useLegend
        useTable
        usePagination
        useSearch
        useSort
        useFilter
        deleteMethod={onDelete}
        customDelete={{ fieldName: "status", comparisonValue: allReportStatus.Custom.id }}
        maxPageSize={DEF_PG_SIZE_LIST_WO_ADD}
        lookup={lookup}>
        <TableContextStateMonitor onFilterChange={onFilterChange} />
        <TableTab
            title={title}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            selectRows={false}
            rowPath={itemPath}
            cyName="cardFormat"
        />
    </TableContextProvider>

};

export default CardFormatTab;
